import React, {
  useEffect,
  useId,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Controller, Control, FieldPath, FieldValues } from "react-hook-form";

import { mayTakeFocus, otpChange } from "./otp-digits";

type OTPInputHandle = { focus: () => void };

type OTPInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  length?: number;
  /** Called with the code when an edit leaves it with all its digits. */
  onComplete?: (code: string) => void;
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: string | null;
  /** Where the code sits in the row: centered, or flush left. */
  align?: "center" | "start";
  /** Tells the error in a box under the input. A screen that tells it in a
   * line of its own turns this off; `error` still marks the input invalid. */
  showError?: boolean;
  /** Shimmers the box while its code is on the way. */
  busy?: boolean;
};

const INPUT_CLASS =
  "block w-full rounded-lg border border-gray-300 bg-white py-3 text-2xl font-bold tabular-nums text-soil shadow-sm focus:border-cantelope focus:outline-none focus:ring-1 focus:ring-cantelope xs:text-3xl";

const IDLE_DISABLED_CLASS = "disabled:bg-gray-100";
const BUSY_CLASS =
  "animate-pulse disabled:bg-oat-100 motion-reduce:animate-none";

// The pad on the left matches the letter spacing after the last digit, so the
// digits sit in the middle of the field.
const ALIGNED_CLASS: Record<
  NonNullable<OTPInputProps<FieldValues>["align"]>,
  string
> = {
  center: "pl-[0.4em] text-center tracking-[0.4em]",
  start: "px-4 text-left tracking-[0.4em]",
};

function OTPInputInner<T extends FieldValues>(
  {
    name,
    control,
    length = 6,
    onComplete,
    onChange,
    disabled = false,
    error,
    align = "center",
    showError = true,
    busy = false,
  }: OTPInputProps<T>,
  ref: React.Ref<OTPInputHandle>,
) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  useEffect(() => {
    if (error) inputRef.current?.focus();
  }, [error]);

  useEffect(() => {
    if (disabled) return;
    const active = document.activeElement;
    const input = inputRef.current;
    const focusable = mayTakeFocus(
      active === null
        ? null
        : { tag: active.tagName, isSelf: active === input },
    );
    if (focusable) input?.focus();
  }, [disabled]);

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange: onFieldChange, value = "" },
        fieldState: { error: fieldError },
      }) => {
        const edit = (raw: string) => {
          const next = otpChange(value, raw, length);
          onFieldChange(next.value);
          onChange?.(next.value);
          if (next.complete) onComplete?.(next.value);
        };

        return (
          <div className="space-y-4">
            <label htmlFor={inputId} className="sr-only">
              Verification code
            </label>
            <input
              id={inputId}
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              value={value}
              onChange={(e) => edit(e.currentTarget.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={disabled}
              aria-busy={busy || undefined}
              aria-invalid={error || fieldError ? true : undefined}
              className={`${INPUT_CLASS} ${ALIGNED_CLASS[align]} ${busy ? BUSY_CLASS : IDLE_DISABLED_CLASS}`}
            />

            {showError && error && (
              <div className="rounded bg-red-100 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {showError && !error && !isFocused && fieldError && (
              <div className="rounded bg-red-100 p-3 text-sm text-red-700">
                {fieldError.message}
              </div>
            )}
          </div>
        );
      }}
    />
  );
}

const OTPInput = forwardRef(OTPInputInner) as <T extends FieldValues>(
  props: OTPInputProps<T> & { ref?: React.Ref<OTPInputHandle> },
) => React.ReactElement;

export default OTPInput;
